import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useIdentity } from '../../identity/state/useIdentity';
import { payrollReviewGateway } from '../index';
import { isForbidden, payrollReviewErrorMessage } from '../payrollReviewErrors';
import { PayrollRunStatus, type PayrollReviewRun } from '../types';
import { PayrollRunStatusBadge } from '../components/PayrollRunStatusBadge';
import { formatDate, formatDateTime, formatPeriod } from '../formatting';
import '../payroll-review.css';

type Filter = 'pending' | 'all';
type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; runs: PayrollReviewRun[] }
  | { kind: 'forbidden' }
  | { kind: 'error'; message: string };

/** EPN-104 — payroll runs for the active company, defaulting to those awaiting approval. The API has no status
 * filter or paging, so the filter is applied here to the (newest-first) list the server returns. Every
 * permission decision is the server's: a 403 simply shows the forbidden panel. */
export function PayrollReviewListPage() {
  const { me } = useIdentity();
  // Switching company changes the identity without navigating or remounting the routed page, which would leave the
  // previous company's run list (periods, statuses, preparer emails) on screen under the new company's name. Keying by
  // the active company remounts the page, so it reloads for the new company and its in-flight request is aborted.
  return <PayrollReviewList key={me?.activeCompanyId ?? 'no-active-company'} />;
}

function PayrollReviewList() {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [filter, setFilter] = useState<Filter>('pending');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setState({ kind: 'loading' });
    payrollReviewGateway
      .listRuns(controller.signal)
      .then((runs) => {
        if (!controller.signal.aborted) setState({ kind: 'ready', runs });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setState(isForbidden(err) ? { kind: 'forbidden' } : { kind: 'error', message: payrollReviewErrorMessage(err, 'Could not load payroll runs.') });
      });
    return () => controller.abort();
  }, [attempt]);

  const visible = useMemo(() => {
    if (state.kind !== 'ready') return [];
    return filter === 'pending' ? state.runs.filter((r) => r.status === PayrollRunStatus.PendingApproval) : state.runs;
  }, [state, filter]);

  const total = state.kind === 'ready' ? state.runs.length : 0;

  return (
    <section className="card card--wide payroll-review">
      <div className="section-header">
        <h2>Payroll Review</h2>
      </div>
      <p className="muted payroll-review__intro">
        Review payroll runs that have been submitted for approval. Approving or rejecting a run records a decision in
        EPN — open a run to see the payroll register and the full notice before deciding.
      </p>

      <div className="payroll-review__filter" role="group" aria-label="Filter payroll runs">
        <button type="button" aria-pressed={filter === 'pending'} onClick={() => setFilter('pending')}>
          Pending approval
        </button>
        <button type="button" aria-pressed={filter === 'all'} onClick={() => setFilter('all')}>
          All
        </button>
      </div>

      {state.kind === 'loading' && (
        <p className="muted" role="status">
          Loading payroll runs…
        </p>
      )}

      {state.kind === 'forbidden' && (
        <div className="payroll-review__panel" role="alert">
          <strong>You don&apos;t have permission to view payroll runs.</strong>
          <p className="muted">Ask a company administrator if you need access to Payroll Review.</p>
        </div>
      )}

      {state.kind === 'error' && (
        <div className="payroll-review__panel payroll-review__panel--error" role="alert">
          <p className="error">{state.message}</p>
          <button type="button" onClick={() => setAttempt((n) => n + 1)}>
            Retry
          </button>
        </div>
      )}

      {state.kind === 'ready' && visible.length === 0 && (
        <p className="muted" role="status">
          {filter === 'all' ? (
            'No payroll runs yet.'
          ) : (
            <>
              No payroll runs awaiting review.
              {total > 0 && (
                <>
                  {' '}
                  <button type="button" className="link" onClick={() => setFilter('all')}>
                    Show all payroll runs
                  </button>
                </>
              )}
            </>
          )}
        </p>
      )}

      {state.kind === 'ready' && visible.length > 0 && (
        <div className="payroll-review__table-scroll" role="region" aria-label="Payroll runs" tabIndex={0}>
          <table>
            <caption className="payroll-review__sr-only">Payroll runs</caption>
            <thead>
              <tr>
                <th scope="col">Pay period</th>
                <th scope="col">Check date</th>
                <th scope="col">Status</th>
                <th scope="col">Created by</th>
                <th scope="col">Submitted by</th>
                <th scope="col">Submitted</th>
                <th scope="col">
                  <span className="payroll-review__sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {visible.map((run) => {
                const period = formatPeriod(run.periodStartDate, run.periodEndDate);
                const pending = run.status === PayrollRunStatus.PendingApproval;
                return (
                  <tr key={run.id}>
                    <th scope="row">{period}</th>
                    <td>{formatDate(run.checkDate)}</td>
                    <td>
                      <PayrollRunStatusBadge status={run.status} />
                    </td>
                    <td>{run.createdByEmailSnapshot}</td>
                    <td>{run.submittedByEmailSnapshot ?? '—'}</td>
                    <td>{formatDateTime(run.submittedForApprovalAtUtc)}</td>
                    <td>
                      <Link to={`/payroll-review/${run.id}`} aria-label={`${pending ? 'Review' : 'View'} payroll run for ${period}`}>
                        {pending ? 'Review' : 'View'}
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
